import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import {
  createEmptyProgress,
  loadStoredProgress,
  saveStoredProgress,
  type LearnerProfile,
  type Progress,
  type QuestionAttempt
} from './progress';

type ProgressActions = {
  finishOnboarding: () => void;
  setStage: (dayId: string, stage: number) => void;
  setChecklist: (checklistId: string, checked: boolean) => void;
  setQuestionAttempt: (questionId: string, attempt: QuestionAttempt) => void;
  setReflection: (fieldId: string, value: string) => void;
  setProfile: (profile: LearnerProfile) => void;
  completeDay: (dayId: string) => void;
  replaceProgress: (progress: Progress) => void;
  resetProgress: () => void;
};

const ProgressStateContext = createContext<Progress | null>(null);
const ProgressActionsContext = createContext<ProgressActions | null>(null);

function stamp(progress: Progress): Progress {
  return { ...progress, updatedAt: new Date().toISOString() };
}

export function ProgressProvider({ children }: { children: ReactNode }) {
  const [progress, setProgress] = useState(loadStoredProgress);

  const update = useCallback((change: (current: Progress) => Progress) => {
    setProgress((current) => {
      const next = stamp(change(current));
      saveStoredProgress(next);
      return next;
    });
  }, []);

  const actions = useMemo<ProgressActions>(
    () => ({
      finishOnboarding: () => {
        update((current) => ({
          ...current,
          onboardingComplete: true,
          startedAt: current.startedAt ?? new Date().toISOString()
        }));
      },
      setStage: (dayId, stage) => {
        update((current) => ({
          ...current,
          stageByDay: { ...current.stageByDay, [dayId]: Math.max(0, Math.min(3, stage)) }
        }));
      },
      setChecklist: (checklistId, checked) => {
        update((current) => ({
          ...current,
          checklist: { ...current.checklist, [checklistId]: checked }
        }));
      },
      setQuestionAttempt: (questionId, attempt) => {
        update((current) => ({
          ...current,
          questionAttempts: { ...current.questionAttempts, [questionId]: attempt }
        }));
      },
      setReflection: (fieldId, value) => {
        update((current) => ({
          ...current,
          reflections: { ...current.reflections, [fieldId]: value }
        }));
      },
      setProfile: (profile) => {
        update((current) => ({
          ...current,
          profile
        }));
      },
      completeDay: (dayId) => {
        update((current) => {
          if (current.completedDayIds.includes(dayId)) return current;
          return {
            ...current,
            completedDayIds: [...current.completedDayIds, dayId],
            completedAtByDay: {
              ...current.completedAtByDay,
              [dayId]: new Date().toISOString()
            }
          };
        });
      },
      replaceProgress: (replacement) => {
        const next = stamp(replacement);
        saveStoredProgress(next);
        setProgress(next);
      },
      resetProgress: () => {
        const empty = createEmptyProgress();
        saveStoredProgress(empty);
        setProgress(empty);
      }
    }),
    [update]
  );

  return (
    <ProgressStateContext value={progress}>
      <ProgressActionsContext value={actions}>{children}</ProgressActionsContext>
    </ProgressStateContext>
  );
}

export function useProgress(): Progress {
  const progress = useContext(ProgressStateContext);
  if (!progress) throw new Error('useProgress must be used inside ProgressProvider.');
  return progress;
}

export function useProgressActions(): ProgressActions {
  const actions = useContext(ProgressActionsContext);
  if (!actions) throw new Error('useProgressActions must be used inside ProgressProvider.');
  return actions;
}
